import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom purple marker for Flix branding
const purpleIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Default position for San José, Costa Rica
const DEFAULT_POSITION = { lat: 9.9281, lng: -84.0907 };

// Component to update map center when position changes
const MapUpdater = ({ position }) => {
  const map = useMap();
  
  useEffect(() => {
    if (position && position.lat && position.lng) {
      map.setView([position.lat, position.lng], map.getZoom());
    }
  }, [position, map]);
  
  return null;
};

const LocationMap = ({ 
  position, 
  zoom = 14, 
  height = "200px", 
  showPopup = true, 
  popupText = "Ubicación del servicio",
  className = "",
  interactive = true
}) => {
  // Memoize map position to prevent unnecessary re-renders
  const mapPosition = useMemo(() => {
    return position && position.lat && position.lng 
      ? { lat: position.lat, lng: position.lng }
      : DEFAULT_POSITION;
  }, [position]);
  
  // Memoize center array for MapContainer
  const centerArray = useMemo(() => [mapPosition.lat, mapPosition.lng], [mapPosition]);
  
  // Memoize marker position array
  const markerPosition = useMemo(() => {
    if (position && position.lat && position.lng) {
      return [position.lat, position.lng];
    }
    return null;
  }, [position]);
  
  return (
    <div className={`rounded-lg overflow-hidden border border-slate-200 ${className}`} style={{ height }}>
      <MapContainer
        center={centerArray}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        zoomControl={interactive}
        doubleClickZoom={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater position={mapPosition} />
        {markerPosition && (
          <Marker position={markerPosition} icon={purpleIcon}>
            {showPopup && (
              <Popup>
                <div className="text-sm">
                  <p className="font-medium text-purple-700">{popupText}</p>
                  {position.address && (
                    <p className="text-slate-600 mt-1">{position.address}</p>
                  )}
                </div>
              </Popup>
            )}
          </Marker>
        )}
      </MapContainer>
    </div>
  );
};

export default LocationMap;
